/**
 * Sent Invites Page (Lead Owner View)
 * Lead Owners track invites they've sent to DSAs
 *
 * Features:
 * - Lazy loading: Awaiting tab loads on page load, Accepted tab loads on click
 * - Awaiting Response tab: Invites pending DSA action (no interest data needed)
 * - Accepted tab: Full negotiation flow UI (Received Interests style)
 * - Lead Owner can review, counter, accept, or reject interests
 * - Declined tab: Declined/expired/cancelled invites
 * - Filters: Search, loan type, date range
 * - Pagination per tab
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { TabView, TabPanel } from 'primereact/tabview';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Divider } from 'primereact/divider';
import { SelectButton } from 'primereact/selectbutton';
import { Tooltip } from 'primereact/tooltip';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Paginator } from 'primereact/paginator';
import {
    dsaOfferService,
    getDsaInviteStatusConfig,
    getDsaLoanTypeConfig,
    formatCommission,
    formatTimeAgo,
    getDeadlineUrgency,
    DSA_LOAN_TYPES
} from '../../services/dsaOfferService';
import { formatLoanAmount, getInterestStatusConfig } from '../../services/marketplaceService';
import ReviewInterestDialog from '../../components/marketplace/interests/ReviewInterestDialog';
import NavigationTabMenu from '../../components/common/NavigationTabMenu';

// Market Payout section tabs
const MARKET_PAYOUT_TABS = [
    { label: 'Discover Payout', icon: 'pi pi-search', route: '/app/dsa-offers' },
    { label: 'My Enquiries', icon: 'pi pi-send', route: '/app/dsa-offers/sent-invites' },
    { label: 'Post Payout', icon: 'pi pi-plus', route: '/app/dsa-offers/post' }
];

// Loan type options for filter
const LOAN_TYPE_OPTIONS = [
    { label: 'All Types', value: null },
    ...DSA_LOAN_TYPES.map(lt => ({ label: lt.label, value: lt.value }))
];

const ROWS_PER_PAGE = 10;

// Outline style for Tags - border only, no solid fill
const outlineTagStyle = (severity) => {
    const colors = {
        warning: { background: 'transparent', border: '1.5px solid #EAB308', color: '#EAB308' },
        info: { background: 'transparent', border: '1.5px solid #3B82F6', color: '#3B82F6' },
        success: { background: 'transparent', border: '1.5px solid #22C55E', color: '#22C55E' },
        danger: { background: 'transparent', border: '1.5px solid #EF4444', color: '#EF4444' },
        secondary: { background: 'transparent', border: '1.5px solid #94A3B8', color: '#64748B' },
    };
    return colors[severity] || colors.secondary;
};

const SentInvites = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Refs to prevent duplicate API calls
    const prevFiltersRef = useRef(null);
    const lastAwaitingFetchKeyRef = useRef('');
    const lastAcceptedFetchKeyRef = useRef('');

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // View mode
    const [viewMode, setViewMode] = useState('grid');
    const viewOptions = [
        { icon: 'pi pi-th-large', value: 'grid' },
        { icon: 'pi pi-list', value: 'list' }
    ];

    // ========== FILTER STATE ==========
    const [showFilters, setShowFilters] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loanTypeFilter, setLoanTypeFilter] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // ========== PAGINATION STATE ==========
    const [awaitingPagination, setAwaitingPagination] = useState({ first: 0, rows: ROWS_PER_PAGE, total: 0 });
    const [acceptedPagination, setAcceptedPagination] = useState({ first: 0, rows: ROWS_PER_PAGE, total: 0 });

    // ========== DATA STATE ==========

    // Awaiting Response tab state (pending + viewed invites)
    const [awaitingInvites, setAwaitingInvites] = useState([]);
    const [awaitingLoading, setAwaitingLoading] = useState(true);

    // Accepted tab state (interested invites with interest data)
    const [acceptedInvites, setAcceptedInvites] = useState([]);
    const [acceptedLoading, setAcceptedLoading] = useState(false);
    const [acceptedLoaded, setAcceptedLoaded] = useState(false);

    // Declined tab state
    const [declinedInvites, setDeclinedInvites] = useState([]);
    const [declinedLoading, setDeclinedLoading] = useState(false);
    const [declinedLoaded, setDeclinedLoaded] = useState(false);

    // Summary counts
    const [summary, setSummary] = useState({
        pending: 0,
        interested: 0,
        declined: 0,
        total: 0
    });

    // Review Interest Dialog
    const [reviewDialogVisible, setReviewDialogVisible] = useState(false);
    const [selectedInterest, setSelectedInterest] = useState(null);

    // ========== DEBOUNCE SEARCH ==========
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    // Check if any filter is active
    const hasActiveFilters = debouncedSearch || loanTypeFilter || startDate || endDate;

    // Clear all filters
    const clearFilters = () => {
        setSearchQuery('');
        setDebouncedSearch('');
        setLoanTypeFilter(null);
        setStartDate(null);
        setEndDate(null);
    };

    // ========== DATA FETCHING ==========

    // Fetch awaiting invites - called by effect
    const fetchAwaitingInvites = async (page, filterParams) => {
        setAwaitingLoading(true);
        try {
            const response = await dsaOfferService.getSentInvites({
                status: 'awaiting',
                include_interest: false,
                per_page: ROWS_PER_PAGE,
                page: page,
                ...filterParams
            });

            if (response.success) {
                const data = response.data || {};
                setAwaitingInvites(data.invites || []);
                setSummary(data.summary || { total: 0, pending: 0, interested: 0, declined: 0 });
                setAwaitingPagination(prev => ({
                    ...prev,
                    total: data.pagination?.total || data.invites?.length || 0
                }));
            }
        } catch (error) {
            console.error('Failed to fetch awaiting invites:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load awaiting invites'
            });
        } finally {
            setAwaitingLoading(false);
        }
    };

    // Fetch accepted invites - called by effect
    const fetchAcceptedInvites = async (page, filterParams) => {
        setAcceptedLoading(true);
        try {
            const response = await dsaOfferService.getSentInvites({
                status: 'interested',
                include_interest: true,
                per_page: ROWS_PER_PAGE,
                page: page,
                ...filterParams
            });

            if (response.success) {
                const data = response.data || {};
                setAcceptedInvites(data.invites || []);
                setAcceptedLoaded(true);
                setAcceptedPagination(prev => ({
                    ...prev,
                    total: data.pagination?.total || data.invites?.length || 0
                }));
            }
        } catch (error) {
            console.error('Failed to fetch accepted invites:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load accepted invites'
            });
        } finally {
            setAcceptedLoading(false);
        }
    };

    // Fetch declined invites (declined, expired, cancelled) - loads on tab click
    const fetchDeclinedInvites = async (force = false) => {
        if (!force && declinedLoaded) return;

        setDeclinedLoading(true);
        try {
            // Fetch declined, expired, and cancelled separately and merge
            const [declinedRes, expiredRes, cancelledRes] = await Promise.all([
                dsaOfferService.getSentInvites({ status: 'declined', include_interest: false }),
                dsaOfferService.getSentInvites({ status: 'expired', include_interest: false }),
                dsaOfferService.getSentInvites({ status: 'cancelled', include_interest: false })
            ]);

            const merged = [
                ...(declinedRes?.data?.invites || []),
                ...(expiredRes?.data?.invites || []),
                ...(cancelledRes?.data?.invites || [])
            ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            setDeclinedInvites(merged);
            setSummary(prev => ({
                ...prev,
                declined: merged.length
            }));
            setDeclinedLoaded(true);
        } catch (error) {
            console.error('Failed to fetch declined invites:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load declined invites'
            });
        } finally {
            setDeclinedLoading(false);
        }
    };

    // Single effect for awaiting invites - prevents double API calls
    useEffect(() => {
        const currentFilters = { debouncedSearch, loanTypeFilter, startDate, endDate };
        const prevFilters = prevFiltersRef.current;

        // Check if filters changed
        const filtersChanged = prevFilters !== null && (
            prevFilters.debouncedSearch !== debouncedSearch ||
            prevFilters.loanTypeFilter !== loanTypeFilter ||
            prevFilters.startDate !== startDate ||
            prevFilters.endDate !== endDate
        );

        // If filters changed and not on first page, reset page and skip fetch
        if (filtersChanged && awaitingPagination.first !== 0) {
            prevFiltersRef.current = currentFilters;
            setAwaitingPagination(prev => ({ ...prev, first: 0 }));
            return;
        }

        // Build filter params
        const filterParams = {};
        if (debouncedSearch) filterParams.search = debouncedSearch;
        if (loanTypeFilter) filterParams.loan_type = loanTypeFilter;
        if (startDate) filterParams.date_from = startDate.toISOString().split('T')[0];
        if (endDate) filterParams.date_to = endDate.toISOString().split('T')[0];

        // Create fetch key
        const fetchKey = JSON.stringify({
            debouncedSearch, loanTypeFilter,
            startDate: startDate?.toISOString(),
            endDate: endDate?.toISOString(),
            first: awaitingPagination.first
        });

        // Skip if same fetch key
        if (fetchKey === lastAwaitingFetchKeyRef.current) {
            return;
        }

        prevFiltersRef.current = currentFilters;
        lastAwaitingFetchKeyRef.current = fetchKey;

        const page = Math.floor(awaitingPagination.first / ROWS_PER_PAGE) + 1;
        fetchAwaitingInvites(page, filterParams);
    }, [debouncedSearch, loanTypeFilter, startDate, endDate, awaitingPagination.first]);

    // Effect for accepted invites - only runs when tab is active (index 1)
    useEffect(() => {
        if (activeTab !== 1) return;
        if (acceptedLoaded) return;

        // Build filter params
        const filterParams = {};
        if (debouncedSearch) filterParams.search = debouncedSearch;
        if (loanTypeFilter) filterParams.loan_type = loanTypeFilter;
        if (startDate) filterParams.date_from = startDate.toISOString().split('T')[0];
        if (endDate) filterParams.date_to = endDate.toISOString().split('T')[0];

        const page = Math.floor(acceptedPagination.first / ROWS_PER_PAGE) + 1;
        fetchAcceptedInvites(page, filterParams);
    }, [activeTab, acceptedLoaded, debouncedSearch, loanTypeFilter, startDate, endDate, acceptedPagination.first]);

    // Tab change handler
    const handleTabChange = (e) => {
        setActiveTab(e.index);

        // Build filter params
        const filterParams = {};
        if (debouncedSearch) filterParams.search = debouncedSearch;
        if (loanTypeFilter) filterParams.loan_type = loanTypeFilter;
        if (startDate) filterParams.date_from = startDate.toISOString().split('T')[0];
        if (endDate) filterParams.date_to = endDate.toISOString().split('T')[0];

        // Force refetch when switching to Awaiting tab (index 0)
        if (e.index === 0) {
            lastAwaitingFetchKeyRef.current = '';
            const page = Math.floor(awaitingPagination.first / ROWS_PER_PAGE) + 1;
            fetchAwaitingInvites(page, filterParams);
        }

        // Force refetch when switching to Accepted tab (index 1)
        if (e.index === 1) {
            lastAcceptedFetchKeyRef.current = '';
            setAcceptedLoaded(false);
        }

        // Load declined tab on first visit (index 2)
        if (e.index === 2 && !declinedLoaded) {
            fetchDeclinedInvites();
        }
    };

    // ========== PAGINATION HANDLERS ==========
    const handleAwaitingPageChange = (e) => {
        setAwaitingPagination(prev => ({ ...prev, first: e.first, rows: e.rows }));
    };

    const handleAcceptedPageChange = (e) => {
        setAcceptedPagination(prev => ({ ...prev, first: e.first, rows: e.rows }));
    };

    // ========== ACTIONS ==========

    // Refresh data
    const handleRefresh = () => {
        // Build filter params
        const filterParams = {};
        if (debouncedSearch) filterParams.search = debouncedSearch;
        if (loanTypeFilter) filterParams.loan_type = loanTypeFilter;
        if (startDate) filterParams.date_from = startDate.toISOString().split('T')[0];
        if (endDate) filterParams.date_to = endDate.toISOString().split('T')[0];

        const awaitingPage = Math.floor(awaitingPagination.first / ROWS_PER_PAGE) + 1;
        fetchAwaitingInvites(awaitingPage, filterParams);

        if (activeTab === 1) {
            setAcceptedLoaded(false);
            const acceptedPage = Math.floor(acceptedPagination.first / ROWS_PER_PAGE) + 1;
            fetchAcceptedInvites(acceptedPage, filterParams);
        }
    };

    // Handle cancel invite
    const handleCancel = (invite) => {
        confirmDialog({
            message: 'Are you sure you want to cancel this enquiry? The payout will no longer be able to accept it.',
            header: 'Cancel Enquiry',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const response = await dsaOfferService.cancelInvite(invite.id);
                    if (response.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Enquiry Cancelled',
                            detail: 'The enquiry has been cancelled'
                        });
                        handleRefresh();
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to cancel enquiry'
                    });
                }
            }
        });
    };

    // Handle opening review dialog for interest
    const handleReviewInterest = (invite) => {
        if (!invite.linked_interest) {
            toast.current?.show({
                severity: 'warn',
                summary: 'No Interest Found',
                detail: 'Interest data not available for this invite'
            });
            return;
        }

        // Transform linked_interest to match ReviewInterestDialog's expected format
        const interest = {
            id: invite.linked_interest.id,
            status: invite.linked_interest.status,
            proposed_percentage: invite.linked_interest.proposed_percentage,
            counter_percentage: invite.linked_interest.counter_percentage,
            final_percentage: invite.linked_interest.final_percentage,
            negotiation_round: invite.linked_interest.negotiation_round,
            interest_message: invite.linked_interest.interest_message,
            is_owner_turn: invite.linked_interest.is_owner_turn,
            is_agent_turn: invite.linked_interest.is_agent_turn,
            requested_role: invite.linked_interest.requested_role,
            contribution_level: invite.linked_interest.contribution_level,
            interested_agent: invite.linked_interest.interested_agent || {
                id: invite.dsa?.id,
                name: invite.dsa?.display_name || invite.dsa?.name,
                avatar: invite.dsa?.avatar
            }
        };

        setSelectedInterest(interest);
        setReviewDialogVisible(true);
    };

    // Handle dialog success (after counter/accept/reject)
    const handleReviewSuccess = () => {
        toast.current?.show({
            severity: 'success',
            summary: 'Action Completed',
            detail: 'Interest has been updated'
        });
        // Refresh accepted invites with force
        lastAcceptedFetchKeyRef.current = '';
        setAcceptedLoaded(false);
    };


    // ========== CARD COMPONENTS ==========

    // Card for awaiting response invites (grid card view - matches Accepted structure)
    const AwaitingInviteCard = ({ invite }) => {
        const statusConfig = getDsaInviteStatusConfig(invite.status);
        const loanTypeConfig = getDsaLoanTypeConfig(invite.dsa_offer?.loan_type);
        const deadlineInfo = getDeadlineUrgency(invite.expires_at);

        const lead = invite.marketplace_lead;
        const offer = invite.dsa_offer;
        const dsa = invite.dsa;

        const borrowerName = lead?.borrower_name || 'Lead';
        const loanAmount = lead?.loan_amount || 0;
        const canCancel = invite.status === 'pending' || invite.status === 'viewed';
        const locationParts = [lead?.city, lead?.state].filter(Boolean);

        return (
            <div className="surface-card border-round shadow-1 overflow-hidden hover:shadow-2 transition-all transition-duration-150 h-full flex flex-column">
                <div className="p-3 flex flex-column gap-2 flex-1">
                    {/* DSA Info */}
                    <div className="flex align-items-center gap-2">
                        <Avatar
                            image={dsa?.avatar}
                            icon={!dsa?.avatar ? 'pi pi-user' : null}
                            size="large"
                            shape="circle"
                            className="bg-green-100 flex-shrink-0"
                            style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.8rem' }}>{dsa?.display_name || dsa?.name || 'DSA'}</div>
                            <div className="flex align-items-center gap-1 flex-wrap">
                                <Tag value={offer?.bank_name} style={{ ...outlineTagStyle('info'), fontSize: '0.6rem', padding: '1px 5px' }} />
                                <Tag value={loanTypeConfig.label} style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '1px 4px' }} />
                            </div>
                        </div>
                    </div>

                    {/* Lead Info */}
                    <div>
                        <div className="text-500" style={{ fontSize: '0.6rem' }}>Lead</div>
                        <div className="font-semibold text-900" style={{ fontSize: '0.8rem' }}>{borrowerName}</div>
                        <div className="flex align-items-center gap-2 mt-1">
                            <span className="text-green-600 font-bold" style={{ fontSize: '0.75rem' }}>{formatLoanAmount(loanAmount)}</span>
                            {lead?.product_type && <Tag value={getDsaLoanTypeConfig(lead.product_type).label} style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '1px 4px' }} />}
                        </div>
                        {locationParts.length > 0 && (
                            <div className="text-500 mt-1" style={{ fontSize: '0.65rem' }}>
                                <i className="pi pi-map-marker mr-1" style={{ fontSize: '0.6rem' }} />{locationParts.join(', ')}
                            </div>
                        )}
                    </div>

                    <Divider className="my-1" />

                    {/* Status row - matches Accepted card structure */}
                    <div className="flex flex-wrap gap-3" style={{ fontSize: '0.75rem' }}>
                        <div>
                            <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Status</div>
                            <Tag value={statusConfig.label} style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.6rem', padding: '2px 5px' }} icon={`pi ${statusConfig.icon}`} />
                        </div>
                        <div>
                            <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>DSA's Offer</div>
                            <div className="font-bold text-primary">{formatCommission(offer?.commission_percentage)}</div>
                        </div>
                        {offer?.bank_name && (
                            <div>
                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Bank</div>
                                <div className="font-semibold text-700" style={{ fontSize: '0.7rem' }}>{offer.bank_name}</div>
                            </div>
                        )}
                        {deadlineInfo.daysLeft !== null && deadlineInfo.level !== 'relaxed' && deadlineInfo.level !== 'expired' && (
                            <div>
                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Expires</div>
                                <Tag value={deadlineInfo.label} style={{ ...outlineTagStyle(deadlineInfo.severity), fontSize: '0.6rem', padding: '2px 5px' }} icon="pi pi-clock" />
                            </div>
                        )}
                    </div>

                    {/* Message - single line truncated */}
                    {invite.invite_message && (
                        <div className="text-500 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.65rem' }}>
                            <i className="pi pi-comment mr-1 text-blue-500" style={{ fontSize: '0.6rem' }} />
                            "{invite.invite_message}"
                        </div>
                    )}

                    {/* Timestamp */}
                    <div className="text-500" style={{ fontSize: '0.6rem' }}>
                        Sent: {formatTimeAgo(invite.invited_at || invite.created_at)}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex align-items-center gap-2 px-3 pb-3 flex-wrap mt-auto">
                    {canCancel && (
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-outlined p-button-danger"
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                            onClick={() => handleCancel(invite)}
                        />
                    )}
                    <div className="flex align-items-center gap-1 ml-auto">
                        <Button
                            icon="pi pi-briefcase"
                            className="p-button-text"
                            style={{ padding: '0.35rem' }}
                            tooltip="View Offer"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => navigate(`/app/dsa-offers/${offer?.id}`)}
                        />
                        <Button
                            icon="pi pi-external-link"
                            className="p-button-text"
                            style={{ padding: '0.35rem' }}
                            tooltip="View Lead"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => navigate(`/app/marketplace/listing/${lead?.id}`)}
                        />
                    </div>
                </div>
            </div>
        );
    };

    // Card for accepted invites with negotiation flow (grid card view)
    const AcceptedInviteCard = ({ invite }) => {
        const loanTypeConfig = getDsaLoanTypeConfig(invite.dsa_offer?.loan_type);
        const interest = invite.linked_interest;
        const interestStatus = interest?.status || 'pending';
        const interestStatusConfig = getInterestStatusConfig(interestStatus);

        const lead = invite.marketplace_lead;
        const offer = invite.dsa_offer;
        const dsa = invite.dsa;

        const borrowerName = lead?.borrower_name || 'Lead';
        const loanAmount = lead?.loan_amount || 0;
        const locationParts = [lead?.city, lead?.state].filter(Boolean);

        // Negotiation state
        const isOwnerTurn = interest?.is_owner_turn;
        const proposedPercentage = interest?.proposed_percentage;
        const counterPercentage = interest?.counter_percentage;
        const effectivePercentage = interest?.effective_percentage || proposedPercentage;
        const negotiationRound = interest?.negotiation_round || 1;

        // Check if collaboration exists
        const hasCollaboration = interest?.collaboration?.id;

        return (
            <div className="surface-card border-round shadow-1 overflow-hidden h-full flex flex-column">
                {/* Turn Indicator - Compact */}
                {interestStatus === 'pending' && isOwnerTurn && (
                    <div className="px-3 pt-3 flex align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
                        <i className="pi pi-exclamation-circle text-orange-500" style={{ fontSize: '0.75rem' }} />
                        <span className="font-semibold text-orange-600">Your turn to respond</span>
                    </div>
                )}
                {interestStatus === 'countered' && isOwnerTurn && (
                    <div className="px-3 pt-3 flex align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
                        <i className="pi pi-sync text-blue-500" style={{ fontSize: '0.75rem' }} />
                        <span className="font-semibold text-blue-600">DSA responded - Review now</span>
                    </div>
                )}
                {interestStatus === 'countered' && !isOwnerTurn && (
                    <div className="px-3 pt-3 flex align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
                        <i className="pi pi-clock text-yellow-600" style={{ fontSize: '0.75rem' }} />
                        <span className="font-semibold text-yellow-700">Waiting for DSA to respond</span>
                    </div>
                )}
                {interestStatus === 'accepted' && hasCollaboration && (
                    <div className="px-3 pt-3 flex align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
                        <i className="pi pi-check-circle text-green-500" style={{ fontSize: '0.75rem' }} />
                        <span className="font-semibold text-green-600">Lead Deal Active</span>
                    </div>
                )}
                {interestStatus === 'rejected' && (
                    <div className="px-3 pt-3 flex align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
                        <i className="pi pi-times-circle text-red-500" style={{ fontSize: '0.75rem' }} />
                        <span className="font-semibold text-red-600">Interest Rejected</span>
                    </div>
                )}

                <div className="p-3 flex flex-column gap-2 flex-1">
                    {/* DSA Info */}
                    <div className="flex align-items-center gap-2">
                        <Avatar
                            image={dsa?.avatar}
                            icon={!dsa?.avatar ? 'pi pi-user' : null}
                            size="large"
                            shape="circle"
                            className="bg-primary-100 flex-shrink-0"
                            style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.8rem' }}>{dsa?.display_name || dsa?.name || 'DSA'}</div>
                            <div className="flex align-items-center gap-1 flex-wrap">
                                <Tag value={offer?.bank_name} style={{ ...outlineTagStyle('info'), fontSize: '0.6rem', padding: '1px 5px' }} />
                                <Tag value={loanTypeConfig.label} style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '1px 4px' }} />
                            </div>
                        </div>
                    </div>

                    {/* Lead Info */}
                    <div>
                        <div className="text-500" style={{ fontSize: '0.6rem' }}>Lead</div>
                        <div className="font-semibold text-900" style={{ fontSize: '0.8rem' }}>{borrowerName}</div>
                        <div className="flex align-items-center gap-2 mt-1">
                            <span className="text-green-600 font-bold" style={{ fontSize: '0.75rem' }}>{formatLoanAmount(loanAmount)}</span>
                            {lead?.product_type && <Tag value={getDsaLoanTypeConfig(lead.product_type).label} style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '1px 4px' }} />}
                        </div>
                        {locationParts.length > 0 && (
                            <div className="text-500 mt-1" style={{ fontSize: '0.65rem' }}>
                                <i className="pi pi-map-marker mr-1" style={{ fontSize: '0.6rem' }} />{locationParts.join(', ')}
                            </div>
                        )}
                    </div>

                    <Divider className="my-1" />

                    {/* Negotiation Status */}
                    <div className="flex flex-wrap gap-3" style={{ fontSize: '0.75rem' }}>
                        <div>
                            <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Status</div>
                            <Tag
                                value={interestStatusConfig.label}
                                style={{ ...outlineTagStyle(interestStatusConfig.severity), fontSize: '0.6rem', padding: '2px 5px' }}
                                icon={`pi ${interestStatusConfig.icon}`}
                            />
                        </div>
                        <div>
                            <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>DSA's Offer</div>
                            <div className="font-bold text-primary">{formatCommission(proposedPercentage)}</div>
                        </div>
                        {counterPercentage && (
                            <div>
                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Your Counter</div>
                                <div className="font-bold text-orange-500">{formatCommission(counterPercentage)}</div>
                            </div>
                        )}
                        {interestStatus === 'accepted' && (
                            <div>
                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Final Rate</div>
                                <div className="font-bold text-green-600">{formatCommission(effectivePercentage)}</div>
                            </div>
                        )}
                        {offer?.bank_name && (
                            <div>
                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Bank</div>
                                <div className="font-semibold text-700" style={{ fontSize: '0.7rem' }}>{offer.bank_name}</div>
                            </div>
                        )}
                        <div className="flex align-items-center">
                            <span className="text-500" style={{ fontSize: '0.6rem' }}>Round</span>
                            <span className="font-semibold text-700 ml-1" style={{ fontSize: '0.75rem' }}>#{negotiationRound}</span>
                        </div>
                    </div>

                    {/* Messages - single line truncated, show latest */}
                    {interest?.interest_message && (
                        <div className="text-500 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.65rem' }}>
                            <i className="pi pi-comment mr-1 text-blue-500" style={{ fontSize: '0.6rem' }} />
                            DSA: "{interest.interest_message}"
                        </div>
                    )}
                    {interest?.counter_message && (
                        <div className="text-500 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.65rem' }}>
                            <i className="pi pi-reply mr-1 text-orange-500" style={{ fontSize: '0.6rem' }} />
                            You: "{interest.counter_message}"
                        </div>
                    )}

                    {/* Timestamp */}
                    <div className="text-500" style={{ fontSize: '0.6rem' }}>
                        Accepted: {formatTimeAgo(invite.responded_at)}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex align-items-center gap-2 px-3 pb-3 flex-wrap mt-auto">
                    {/* Review/Respond Button */}
                    {(interestStatus === 'pending' || (interestStatus === 'countered' && isOwnerTurn)) && (
                        <Button
                            label={isOwnerTurn ? 'Review' : 'Details'}
                            icon={isOwnerTurn ? 'pi pi-reply' : 'pi pi-eye'}
                            className={isOwnerTurn ? 'p-button-warning' : 'p-button-outlined'}
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                            onClick={() => handleReviewInterest(invite)}
                        />
                    )}

                    {/* Waiting state */}
                    {interestStatus === 'countered' && !isOwnerTurn && (
                        <Button
                            label="Awaiting DSA"
                            icon="pi pi-clock"
                            className="p-button-secondary p-button-outlined"
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                            disabled
                        />
                    )}

                    {/* Collaboration link */}
                    {hasCollaboration && (
                        <Button
                            label="View Lead Deal"
                            icon="pi pi-users"
                            className="p-button-success"
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                            onClick={() => navigate(`/app/collaborations/${interest.collaboration.id}`)}
                        />
                    )}

                    <div className="flex align-items-center gap-1 ml-auto">
                        <Button
                            icon="pi pi-briefcase"
                            className="p-button-text"
                            style={{ padding: '0.35rem' }}
                            tooltip="View Offer"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => navigate(`/app/dsa-offers/${offer?.id}`)}
                        />
                        <Button
                            icon="pi pi-external-link"
                            className="p-button-text"
                            style={{ padding: '0.35rem' }}
                            tooltip="View Lead"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => navigate(`/app/marketplace/listing/${lead?.id}`)}
                        />
                    </div>
                </div>
            </div>
        );
    };

    // Card for declined/expired/cancelled invites (grid card view - matches Accepted structure)
    const DeclinedInviteCard = ({ invite }) => {
        const statusConfig = getDsaInviteStatusConfig(invite.status);
        const loanTypeConfig = getDsaLoanTypeConfig(invite.dsa_offer?.loan_type);

        const lead = invite.marketplace_lead;
        const offer = invite.dsa_offer;
        const dsa = invite.dsa;

        const borrowerName = lead?.borrower_name || 'Lead';
        const loanAmount = lead?.loan_amount || 0;
        const locationParts = [lead?.city, lead?.state].filter(Boolean);

        return (
            <div className="surface-card border-round shadow-1 overflow-hidden opacity-80 h-full flex flex-column">
                <div className="p-3 flex flex-column gap-2 flex-1">
                    {/* DSA Info */}
                    <div className="flex align-items-center gap-2">
                        <Avatar
                            image={dsa?.avatar}
                            icon={!dsa?.avatar ? 'pi pi-user' : null}
                            size="large"
                            shape="circle"
                            className="bg-gray-100 flex-shrink-0"
                            style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-700 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.8rem' }}>{dsa?.display_name || dsa?.name || 'DSA'}</div>
                            <div className="flex align-items-center gap-1 flex-wrap">
                                <Tag value={offer?.bank_name} style={{ ...outlineTagStyle('secondary'), fontSize: '0.6rem', padding: '1px 5px' }} />
                                <Tag value={loanTypeConfig.label} style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '1px 4px' }} />
                            </div>
                        </div>
                    </div>

                    {/* Lead Info */}
                    <div>
                        <div className="text-500" style={{ fontSize: '0.6rem' }}>Lead</div>
                        <div className="font-semibold text-700" style={{ fontSize: '0.8rem' }}>{borrowerName}</div>
                        <div className="flex align-items-center gap-2 mt-1">
                            <span className="text-600" style={{ fontSize: '0.75rem' }}>{formatLoanAmount(loanAmount)}</span>
                            {lead?.product_type && <Tag value={getDsaLoanTypeConfig(lead.product_type).label} style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '1px 4px' }} />}
                        </div>
                        {locationParts.length > 0 && (
                            <div className="text-500 mt-1" style={{ fontSize: '0.65rem' }}>
                                <i className="pi pi-map-marker mr-1" style={{ fontSize: '0.6rem' }} />{locationParts.join(', ')}
                            </div>
                        )}
                    </div>

                    <Divider className="my-1" />

                    {/* Status row - matches Accepted card structure */}
                    <div className="flex flex-wrap gap-3" style={{ fontSize: '0.75rem' }}>
                        <div>
                            <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Status</div>
                            <Tag value={statusConfig.label} style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.6rem', padding: '2px 5px' }} icon={`pi ${statusConfig.icon}`} />
                        </div>
                        <div>
                            <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>DSA's Offer</div>
                            <div className="font-semibold text-600">{formatCommission(offer?.commission_percentage)}</div>
                        </div>
                        {offer?.bank_name && (
                            <div>
                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Bank</div>
                                <div className="font-semibold text-600" style={{ fontSize: '0.7rem' }}>{offer.bank_name}</div>
                            </div>
                        )}
                    </div>

                    {/* Decline reason */}
                    {invite.decline_reason && (
                        <div className="bg-red-50 border-round p-2 text-700" style={{ fontSize: '0.65rem' }}>
                            <i className="pi pi-info-circle mr-1 text-red-500" style={{ fontSize: '0.6rem' }} />
                            Reason: "{invite.decline_reason}"
                        </div>
                    )}

                    {/* Timestamp */}
                    <div className="text-500" style={{ fontSize: '0.6rem' }}>
                        Sent: {formatTimeAgo(invite.invited_at || invite.created_at)}{invite.responded_at ? ` · ${invite.status === 'declined' ? 'Declined' : 'Ended'}: ${formatTimeAgo(invite.responded_at)}` : ''}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex align-items-center gap-2 px-3 pb-3 flex-wrap mt-auto">
                    <div className="flex align-items-center gap-1 ml-auto">
                        <Button
                            icon="pi pi-briefcase"
                            className="p-button-text"
                            style={{ padding: '0.35rem' }}
                            tooltip="View Offer"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => navigate(`/app/dsa-offers/${offer?.id}`)}
                        />
                        <Button
                            icon="pi pi-external-link"
                            className="p-button-text"
                            style={{ padding: '0.35rem' }}
                            tooltip="View Lead"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => navigate(`/app/marketplace/listing/${lead?.id}`)}
                        />
                    </div>
                </div>
            </div>
        );
    };

    // ========== LIST VIEW ROW COMPONENTS ==========

    // Row for awaiting invites (list view)
    const AwaitingInviteRow = ({ invite }) => {
        const statusConfig = getDsaInviteStatusConfig(invite.status);
        const loanTypeConfig = getDsaLoanTypeConfig(invite.dsa_offer?.loan_type);
        const deadlineInfo = getDeadlineUrgency(invite.expires_at);
        const lead = invite.marketplace_lead;
        const offer = invite.dsa_offer;
        const dsa = invite.dsa;
        const borrowerName = lead?.borrower_name || 'Lead';
        const canCancel = invite.status === 'pending' || invite.status === 'viewed';

        return (
            <div className="surface-card border-round shadow-1 mb-2 overflow-hidden border-left-3 border-yellow-400">
                <div className="p-2">
                    <div className="flex flex-column md:flex-row gap-2">
                        {/* Left: DSA + Lead Info */}
                        <div className="flex align-items-start gap-2 flex-1">
                            <Avatar
                                image={dsa?.avatar}
                                icon={!dsa?.avatar ? 'pi pi-user' : null}
                                shape="circle"
                                className="bg-green-100 flex-shrink-0"
                                style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
                            />
                            <div className="flex-1 min-w-0">
                                {/* DSA Name + Status + Type */}
                                <div className="flex align-items-center gap-1 mb-1 flex-wrap">
                                    <span className="font-semibold text-900" style={{ fontSize: '0.75rem' }}>{dsa?.display_name || dsa?.name || 'DSA'}</span>
                                    <Tag value={statusConfig.label} style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.55rem', padding: '0.1rem 0.25rem' }} />
                                    <Tag value={loanTypeConfig.label} style={{ fontSize: '0.5rem', padding: '0 0.25rem', backgroundColor: 'var(--primary-100)', color: 'var(--primary-700)', border: 'none' }} />
                                </div>
                                {/* Lead + Amount + Location */}
                                <div className="flex align-items-center gap-2 text-500 flex-wrap" style={{ fontSize: '0.65rem' }}>
                                    <span><i className="pi pi-file mr-1" style={{ fontSize: '0.55rem' }} />{borrowerName}</span>
                                    <span className="text-green-600 font-bold">{formatLoanAmount(lead?.loan_amount)}</span>
                                    {(lead?.city || lead?.state) && (
                                        <span className="flex align-items-center gap-1 hidden md:inline-flex">
                                            <i className="pi pi-map-marker" style={{ fontSize: '0.55rem' }} />
                                            {[lead?.city, lead?.state].filter(Boolean).join(', ')}
                                        </span>
                                    )}
                                </div>
                                {/* Message */}
                                {invite.invite_message && (
                                    <div className="text-500 mt-1 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.6rem' }}>
                                        "{invite.invite_message}"
                                    </div>
                                )}
                                {/* Meta on desktop */}
                                <div className="hidden md:flex align-items-center gap-2 text-400 mt-1" style={{ fontSize: '0.6rem' }}>
                                    <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                                    <span>Sent {formatTimeAgo(invite.invited_at || invite.created_at)}</span>
                                    {offer?.bank_name && (<><span>·</span><span>{offer.bank_name}</span></>)}
                                    {deadlineInfo.daysLeft !== null && deadlineInfo.level !== 'relaxed' && deadlineInfo.level !== 'expired' && (
                                        <><span>·</span><span className="text-orange-500">Expires {deadlineInfo.label}</span></>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Center: Commission */}
                        <div className="flex md:flex-column align-items-center justify-content-center gap-1 md:px-2 md:border-left-1 md:border-right-1 surface-border">
                            <div className="flex flex-column align-items-center">
                                <div className="text-500" style={{ fontSize: '0.55rem' }}>DSA's Offer</div>
                                <div className="bg-blue-50 border-round px-2 py-1">
                                    <span className="text-primary font-bold" style={{ fontSize: '0.75rem' }}>{formatCommission(offer?.commission_percentage)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Right: Actions */}
                        <div className="flex md:flex-column align-items-center md:align-items-end justify-content-between gap-1">
                            <div className="flex md:hidden align-items-center gap-2 text-400" style={{ fontSize: '0.6rem' }}>
                                <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                                <span>{formatTimeAgo(invite.invited_at || invite.created_at)}</span>
                            </div>
                            <div className="flex align-items-center gap-1">
                                {canCancel && (
                                    <Button label="Cancel" icon="pi pi-times" className="p-button-outlined p-button-danger" style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }} onClick={() => handleCancel(invite)} />
                                )}
                                <Button icon="pi pi-briefcase" className="p-button-outlined" style={{ padding: '0.25rem 0.4rem', fontSize: '0.65rem' }} tooltip="View Offer" tooltipOptions={{ position: 'top' }} onClick={() => navigate(`/app/dsa-offers/${offer?.id}`)} />
                                <Button icon="pi pi-external-link" className="p-button-outlined" style={{ padding: '0.25rem 0.4rem', fontSize: '0.65rem' }} tooltip="View Lead" tooltipOptions={{ position: 'top' }} onClick={() => navigate(`/app/marketplace/listing/${lead?.id}`)} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Row for accepted invites (list view)
    const AcceptedInviteRow = ({ invite }) => {
        const loanTypeConfig = getDsaLoanTypeConfig(invite.dsa_offer?.loan_type);
        const interest = invite.linked_interest;
        const interestStatus = interest?.status || 'pending';
        const interestStatusConfig = getInterestStatusConfig(interestStatus);
        const lead = invite.marketplace_lead;
        const offer = invite.dsa_offer;
        const dsa = invite.dsa;
        const borrowerName = lead?.borrower_name || 'Lead';
        const isOwnerTurn = interest?.is_owner_turn;
        const proposedPercentage = interest?.proposed_percentage;
        const counterPercentage = interest?.counter_percentage;
        const effectivePercentage = interest?.effective_percentage || proposedPercentage;
        const negotiationRound = interest?.negotiation_round || 1;
        const hasCollaboration = interest?.collaboration?.id;

        const getBorderClass = () => {
            if (interestStatus === 'accepted') return 'border-green-400';
            if (interestStatus === 'countered' && isOwnerTurn) return 'border-orange-400';
            if (interestStatus === 'rejected') return 'border-red-400';
            return 'border-blue-400';
        };

        return (
            <div className={`surface-card border-round shadow-1 mb-2 overflow-hidden border-left-3 ${getBorderClass()}`}>
                <div className="p-2">
                    <div className="flex flex-column md:flex-row gap-2">
                        {/* Left: DSA + Lead Info */}
                        <div className="flex align-items-start gap-2 flex-1">
                            <Avatar
                                image={dsa?.avatar}
                                icon={!dsa?.avatar ? 'pi pi-user' : null}
                                shape="circle"
                                className="bg-primary-100 flex-shrink-0"
                                style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
                            />
                            <div className="flex-1 min-w-0">
                                {/* DSA Name + Status + Type */}
                                <div className="flex align-items-center gap-1 mb-1 flex-wrap">
                                    <span className="font-semibold text-900" style={{ fontSize: '0.75rem' }}>{dsa?.display_name || dsa?.name || 'DSA'}</span>
                                    <Tag value={interestStatusConfig.label} style={{ ...outlineTagStyle(interestStatusConfig.severity), fontSize: '0.55rem', padding: '0.1rem 0.25rem' }} />
                                    <Tag value={loanTypeConfig.label} style={{ fontSize: '0.5rem', padding: '0 0.25rem', backgroundColor: 'var(--primary-100)', color: 'var(--primary-700)', border: 'none' }} />
                                </div>
                                {/* Lead + Amount + Location */}
                                <div className="flex align-items-center gap-2 text-500 flex-wrap" style={{ fontSize: '0.65rem' }}>
                                    <span><i className="pi pi-file mr-1" style={{ fontSize: '0.55rem' }} />{borrowerName}</span>
                                    <span className="text-green-600 font-bold">{formatLoanAmount(lead?.loan_amount)}</span>
                                    {(lead?.city || lead?.state) && (
                                        <span className="flex align-items-center gap-1 hidden md:inline-flex">
                                            <i className="pi pi-map-marker" style={{ fontSize: '0.55rem' }} />
                                            {[lead?.city, lead?.state].filter(Boolean).join(', ')}
                                        </span>
                                    )}
                                </div>
                                {/* Messages */}
                                {interest?.interest_message && (
                                    <div className="text-500 mt-1 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.6rem' }}>
                                        DSA: "{interest.interest_message}"
                                    </div>
                                )}
                                {interest?.counter_message && (
                                    <div className="text-500 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.6rem' }}>
                                        You: "{interest.counter_message}"
                                    </div>
                                )}
                                {/* Meta on desktop */}
                                <div className="hidden md:flex align-items-center gap-2 text-400 mt-1" style={{ fontSize: '0.6rem' }}>
                                    <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                                    <span>Accepted {formatTimeAgo(invite.responded_at)}</span>
                                    {offer?.bank_name && (<><span>·</span><span>{offer.bank_name}</span></>)}
                                    {negotiationRound > 1 && (<><span>·</span><span>Round #{negotiationRound}</span></>)}
                                </div>
                            </div>
                        </div>

                        {/* Center: Percentages */}
                        <div className="flex md:flex-column align-items-center justify-content-center gap-1 md:px-2 md:border-left-1 md:border-right-1 surface-border">
                            <div className="flex flex-column align-items-center">
                                <div className="text-500" style={{ fontSize: '0.55rem' }}>DSA's Offer</div>
                                <div className="bg-blue-50 border-round px-2 py-1">
                                    <span className="text-primary font-bold" style={{ fontSize: '0.75rem' }}>{formatCommission(proposedPercentage)}</span>
                                </div>
                            </div>
                            {counterPercentage && (
                                <div className="flex flex-column align-items-center">
                                    <div className="text-500" style={{ fontSize: '0.55rem' }}>Your Counter</div>
                                    <div className="bg-orange-50 border-round px-2 py-1">
                                        <span className="text-orange-600 font-bold" style={{ fontSize: '0.75rem' }}>{formatCommission(counterPercentage)}</span>
                                    </div>
                                </div>
                            )}
                            {interestStatus === 'accepted' && (
                                <div className="flex flex-column align-items-center">
                                    <div className="text-500" style={{ fontSize: '0.55rem' }}>Final</div>
                                    <div className="bg-green-50 border-round px-2 py-1">
                                        <span className="text-green-600 font-bold" style={{ fontSize: '0.75rem' }}>{formatCommission(effectivePercentage)}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right: Actions */}
                        <div className="flex md:flex-column align-items-center md:align-items-end justify-content-between gap-1">
                            <div className="flex md:hidden align-items-center gap-2 text-400" style={{ fontSize: '0.6rem' }}>
                                <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                                <span>{formatTimeAgo(invite.responded_at)}</span>
                            </div>
                            <div className="flex align-items-center gap-1">
                                {(interestStatus === 'pending' || (interestStatus === 'countered' && isOwnerTurn)) && (
                                    <Button label={isOwnerTurn ? 'Review' : 'Details'} icon={isOwnerTurn ? 'pi pi-reply' : 'pi pi-eye'} className={isOwnerTurn ? 'p-button-warning' : 'p-button-outlined'} style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }} onClick={() => handleReviewInterest(invite)} />
                                )}
                                {interestStatus === 'countered' && !isOwnerTurn && (
                                    <Button label="Awaiting" icon="pi pi-clock" className="p-button-outlined p-button-secondary" style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }} disabled />
                                )}
                                {hasCollaboration && (
                                    <Button label="Deal" icon="pi pi-users" className="p-button-success" style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }} onClick={() => navigate(`/app/collaborations/${interest.collaboration.id}`)} />
                                )}
                                <Button icon="pi pi-briefcase" className="p-button-outlined" style={{ padding: '0.25rem 0.4rem', fontSize: '0.65rem' }} tooltip="View Offer" tooltipOptions={{ position: 'top' }} onClick={() => navigate(`/app/dsa-offers/${offer?.id}`)} />
                                <Button icon="pi pi-external-link" className="p-button-outlined" style={{ padding: '0.25rem 0.4rem', fontSize: '0.65rem' }} tooltip="View Lead" tooltipOptions={{ position: 'top' }} onClick={() => navigate(`/app/marketplace/listing/${lead?.id}`)} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Turn indicator footer */}
                {interestStatus === 'countered' && isOwnerTurn && (
                    <div className="bg-orange-50 px-2 py-1 border-top-1 surface-border">
                        <div className="flex align-items-center gap-1 text-orange-700" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-sync" style={{ fontSize: '0.55rem' }} />
                            <span>DSA responded - Review now</span>
                        </div>
                    </div>
                )}
                {interestStatus === 'countered' && !isOwnerTurn && (
                    <div className="bg-yellow-50 px-2 py-1 border-top-1 surface-border">
                        <div className="flex align-items-center gap-1 text-yellow-700" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                            <span>Waiting for DSA to respond</span>
                        </div>
                    </div>
                )}
                {interestStatus === 'accepted' && hasCollaboration && (
                    <div className="bg-green-50 px-2 py-1 border-top-1 surface-border">
                        <div className="flex align-items-center gap-1 text-green-700" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-check-circle" style={{ fontSize: '0.55rem' }} />
                            <span>Lead Deal Active · Final {formatCommission(effectivePercentage)}</span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Row for declined invites (list view)
    const DeclinedInviteRow = ({ invite }) => {
        const statusConfig = getDsaInviteStatusConfig(invite.status);
        const loanTypeConfig = getDsaLoanTypeConfig(invite.dsa_offer?.loan_type);
        const lead = invite.marketplace_lead;
        const offer = invite.dsa_offer;
        const dsa = invite.dsa;
        const borrowerName = lead?.borrower_name || 'Lead';

        return (
            <div className="surface-card border-round shadow-1 mb-2 overflow-hidden opacity-80 border-left-3 border-red-300">
                <div className="p-2">
                    <div className="flex flex-column md:flex-row gap-2">
                        {/* Left: DSA + Lead Info */}
                        <div className="flex align-items-start gap-2 flex-1">
                            <Avatar
                                image={dsa?.avatar}
                                icon={!dsa?.avatar ? 'pi pi-user' : null}
                                shape="circle"
                                className="bg-gray-100 flex-shrink-0"
                                style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
                            />
                            <div className="flex-1 min-w-0">
                                {/* DSA Name + Status + Type */}
                                <div className="flex align-items-center gap-1 mb-1 flex-wrap">
                                    <span className="font-semibold text-700" style={{ fontSize: '0.75rem' }}>{dsa?.display_name || dsa?.name || 'DSA'}</span>
                                    <Tag value={statusConfig.label} style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.55rem', padding: '0.1rem 0.25rem' }} />
                                    <Tag value={loanTypeConfig.label} style={{ fontSize: '0.5rem', padding: '0 0.25rem', backgroundColor: 'var(--primary-100)', color: 'var(--primary-700)', border: 'none' }} />
                                </div>
                                {/* Lead + Amount + Location */}
                                <div className="flex align-items-center gap-2 text-500 flex-wrap" style={{ fontSize: '0.65rem' }}>
                                    <span><i className="pi pi-file mr-1" style={{ fontSize: '0.55rem' }} />{borrowerName}</span>
                                    <span className="text-600 font-bold">{formatLoanAmount(lead?.loan_amount)}</span>
                                    {(lead?.city || lead?.state) && (
                                        <span className="flex align-items-center gap-1 hidden md:inline-flex">
                                            <i className="pi pi-map-marker" style={{ fontSize: '0.55rem' }} />
                                            {[lead?.city, lead?.state].filter(Boolean).join(', ')}
                                        </span>
                                    )}
                                </div>
                                {/* Meta on desktop */}
                                <div className="hidden md:flex align-items-center gap-2 text-400 mt-1" style={{ fontSize: '0.6rem' }}>
                                    <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                                    <span>Sent {formatTimeAgo(invite.invited_at || invite.created_at)}</span>
                                    {invite.responded_at && (<><span>·</span><span>{invite.status === 'declined' ? 'Declined' : 'Ended'} {formatTimeAgo(invite.responded_at)}</span></>)}
                                    {offer?.bank_name && (<><span>·</span><span>{offer.bank_name}</span></>)}
                                </div>
                            </div>
                        </div>

                        {/* Center: Commission */}
                        <div className="flex md:flex-column align-items-center justify-content-center gap-1 md:px-2 md:border-left-1 md:border-right-1 surface-border">
                            <div className="flex flex-column align-items-center">
                                <div className="text-500" style={{ fontSize: '0.55rem' }}>DSA's Offer</div>
                                <div className="bg-gray-50 border-round px-2 py-1">
                                    <span className="text-600 font-bold" style={{ fontSize: '0.75rem' }}>{formatCommission(offer?.commission_percentage)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Right: Actions */}
                        <div className="flex md:flex-column align-items-center md:align-items-end justify-content-between gap-1">
                            <div className="flex md:hidden align-items-center gap-2 text-400" style={{ fontSize: '0.6rem' }}>
                                <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                                <span>{formatTimeAgo(invite.responded_at || invite.created_at)}</span>
                            </div>
                            <div className="flex align-items-center gap-1">
                                <Button icon="pi pi-briefcase" className="p-button-outlined" style={{ padding: '0.25rem 0.4rem', fontSize: '0.65rem' }} tooltip="View Offer" tooltipOptions={{ position: 'top' }} onClick={() => navigate(`/app/dsa-offers/${offer?.id}`)} />
                                <Button icon="pi pi-external-link" className="p-button-outlined" style={{ padding: '0.25rem 0.4rem', fontSize: '0.65rem' }} tooltip="View Lead" tooltipOptions={{ position: 'top' }} onClick={() => navigate(`/app/marketplace/listing/${lead?.id}`)} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Decline reason footer */}
                {invite.decline_reason && (
                    <div className="bg-red-50 px-2 py-1 border-top-1 surface-border">
                        <div className="flex align-items-center gap-1 text-red-600" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-info-circle" style={{ fontSize: '0.55rem' }} />
                            <span>Reason: {invite.decline_reason}</span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ========== RENDERING HELPERS ==========

    // Loading skeleton - viewMode aware
    const renderSkeleton = () => viewMode === 'list' ? (
        <div className="flex flex-column">
            {[1, 2, 3, 4].map(i => (
                <div key={i} className="surface-card border-round shadow-1 mb-2 p-2">
                    <div className="flex align-items-center gap-2">
                        <Skeleton shape="circle" size="1.75rem" />
                        <div className="flex-1">
                            <Skeleton width="60%" height="0.8rem" className="mb-1" />
                            <Skeleton width="80%" height="0.65rem" />
                        </div>
                        <Skeleton width="60px" height="1.75rem" />
                        <Skeleton width="60px" height="1.75rem" />
                    </div>
                </div>
            ))}
        </div>
    ) : (
        <div className="grid">
            {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="col-12 md:col-6 lg:col-4 p-2">
                    <div className="surface-card border-round shadow-1 p-3">
                        <div className="flex align-items-center gap-2 mb-2">
                            <Skeleton shape="circle" size="1.75rem" />
                            <div>
                                <Skeleton width="80px" height="0.8rem" className="mb-1" />
                                <Skeleton width="60px" height="1rem" />
                            </div>
                        </div>
                        <div className="flex gap-2 mb-2">
                            <Skeleton width="60px" height="1.25rem" />
                            <Skeleton width="70px" height="1.25rem" />
                        </div>
                        <Skeleton width="100%" height="0.8rem" className="mb-1" />
                        <Skeleton width="60%" height="0.75rem" className="mb-1" />
                        <Skeleton width="80%" height="0.75rem" className="mb-2" />
                        <div className="flex gap-2">
                            <Skeleton width="60px" height="1.75rem" />
                            <Skeleton width="28px" height="1.75rem" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    // Empty state
    const renderEmpty = (type) => {
        const configs = {
            awaiting: {
                icon: 'pi-send',
                title: 'No Pending Enquiries',
                message: hasActiveFilters
                    ? 'No enquiries match your current filters. Try adjusting your search criteria.'
                    : 'You don\'t have any enquiries awaiting payout response.'
            },
            accepted: {
                icon: 'pi-check-circle',
                title: 'No Accepted Enquiries',
                message: hasActiveFilters
                    ? 'No accepted enquiries match your current filters. Try adjusting your search criteria.'
                    : 'No payouts have accepted your enquiries yet.'
            },
            declined: {
                icon: 'pi-times-circle',
                title: 'No Declined Enquiries',
                message: 'No declined, expired, or cancelled enquiries.'
            }
        };

        const config = configs[type];

        return (
            <div className="flex flex-column align-items-center justify-content-center py-4">
                <div
                    className="border-circle p-3 mb-2 flex align-items-center justify-content-center"
                    style={{ backgroundColor: 'var(--primary-50)', width: '3rem', height: '3rem' }}
                >
                    <i className={`pi ${config.icon} text-primary`} style={{ fontSize: '1.25rem' }} />
                </div>
                <h3 className="text-900 font-semibold mb-1 m-0" style={{ fontSize: '0.85rem' }}>{config.title}</h3>
                <p className="text-500 mb-2 text-center px-3 line-height-3 m-0" style={{ maxWidth: '260px', fontSize: '0.7rem' }}>
                    {config.message}
                </p>
                {hasActiveFilters ? (
                    <Button
                        label="Clear Filters"
                        icon="pi pi-filter-slash"
                        className="p-button-outlined"
                        style={{ fontSize: '0.7rem', padding: '0.3rem 0.5rem' }}
                        onClick={clearFilters}
                    />
                ) : (
                    <Button
                        label="Browse Payout Offers"
                        icon="pi pi-search"
                        style={{ fontSize: '0.7rem', padding: '0.3rem 0.5rem' }}
                        onClick={() => navigate('/app/dsa-offers')}
                    />
                )}
            </div>
        );
    };

    // Render awaiting invites tab content
    const renderAwaitingInvites = () => {
        if (awaitingLoading) return renderSkeleton();

        if (awaitingInvites.length === 0) return renderEmpty('awaiting');

        return (
            <>
                {viewMode === 'list' ? (
                    <div className="flex flex-column">
                        {awaitingInvites.map(invite => (
                            <AwaitingInviteRow key={invite.id} invite={invite} />
                        ))}
                    </div>
                ) : (
                    <div className="grid">
                        {awaitingInvites.map(invite => (
                            <div key={invite.id} className="col-12 md:col-6 lg:col-4 p-2">
                                <AwaitingInviteCard invite={invite} />
                            </div>
                        ))}
                    </div>
                )}
                {awaitingPagination.total > awaitingPagination.rows && (
                    <div className="mt-2">
                        <Paginator
                            first={awaitingPagination.first}
                            rows={awaitingPagination.rows}
                            totalRecords={awaitingPagination.total}
                            onPageChange={handleAwaitingPageChange}
                            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                            className="justify-content-center"
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                            pt={{
                                root: { style: { padding: '0.25rem' } },
                                pageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                firstPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                lastPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                previousPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                nextPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } }
                            }}
                        />
                    </div>
                )}
            </>
        );
    };

    // Render accepted invites tab content
    const renderAcceptedInvites = () => {
        if (acceptedLoading) return renderSkeleton();

        if (acceptedInvites.length === 0) return renderEmpty('accepted');

        return (
            <>
                {viewMode === 'list' ? (
                    <div className="flex flex-column">
                        {acceptedInvites.map(invite => (
                            <AcceptedInviteRow key={invite.id} invite={invite} />
                        ))}
                    </div>
                ) : (
                    <div className="grid">
                        {acceptedInvites.map(invite => (
                            <div key={invite.id} className="col-12 md:col-6 lg:col-4 p-2">
                                <AcceptedInviteCard invite={invite} />
                            </div>
                        ))}
                    </div>
                )}
                {acceptedPagination.total > acceptedPagination.rows && (
                    <div className="mt-2">
                        <Paginator
                            first={acceptedPagination.first}
                            rows={acceptedPagination.rows}
                            totalRecords={acceptedPagination.total}
                            onPageChange={handleAcceptedPageChange}
                            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                            className="justify-content-center"
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                            pt={{
                                root: { style: { padding: '0.25rem' } },
                                pageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                firstPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                lastPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                previousPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                nextPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } }
                            }}
                        />
                    </div>
                )}
            </>
        );
    };

    // ========== MAIN RENDER ==========

    return (
        <div className="sent-invites">
            <NavigationTabMenu tabs={MARKET_PAYOUT_TABS} />
            <Toast ref={toast} />
            <ConfirmDialog />
            <Tooltip target=".tooltip-target" />

            {/* Page Header - Compact */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <div className="flex align-items-center justify-content-between">
                    <h2 className="text-900 font-bold m-0" style={{ fontSize: '1rem' }}>My Enquiries</h2>
                    <div className="flex align-items-center gap-1">
                        <Button
                            icon="pi pi-filter"
                            className={`p-button-text p-button-sm ${hasActiveFilters ? 'p-button-warning' : ''}`}
                            style={{ fontSize: '0.7rem', width: '1.75rem', height: '1.75rem' }}
                            onClick={() => setShowFilters(!showFilters)}
                            tooltip="Filters"
                            badge={hasActiveFilters ? '!' : null}
                        />
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-text p-button-sm"
                            style={{ fontSize: '0.7rem', width: '1.75rem', height: '1.75rem' }}
                            onClick={handleRefresh}
                            loading={awaitingLoading || acceptedLoading}
                            tooltip="Refresh"
                        />
                    </div>
                </div>
            </div>

            {/* Filter Panel - Compact flex layout */}
            {showFilters && (
                <div className="surface-card border-round shadow-1 p-2 mb-2">
                    <div className="flex align-items-end gap-2 flex-wrap">
                        {/* Search */}
                        <div className="flex-1" style={{ minWidth: '140px', maxWidth: '200px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>Search</label>
                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-search" style={{ fontSize: '0.7rem' }} />
                                <InputText
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Lead name, DSA..."
                                    className="w-full p-inputtext-sm"
                                    style={{ fontSize: '0.7rem', paddingLeft: '2rem' }}
                                />
                            </span>
                        </div>

                        {/* Loan Type */}
                        <div className="flex-1" style={{ minWidth: '110px', maxWidth: '150px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>Loan Type</label>
                            <Dropdown
                                value={loanTypeFilter}
                                options={LOAN_TYPE_OPTIONS}
                                onChange={(e) => setLoanTypeFilter(e.value)}
                                placeholder="All Types"
                                optionLabel="label"
                                optionValue="value"
                                showClear
                                className="w-full p-inputtext-sm"
                                panelStyle={{ fontSize: '0.7rem' }}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                            />
                        </div>

                        {/* Start Date */}
                        <div className="flex-1" style={{ minWidth: '120px', maxWidth: '160px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>From Date</label>
                            <Calendar
                                value={startDate}
                                onChange={(e) => setStartDate(e.value)}
                                placeholder="From"
                                className="w-full p-inputtext-sm"
                                showIcon
                                inputStyle={{ fontSize: '0.7rem' }}
                                dateFormat="dd M yy"
                                showButtonBar
                                maxDate={endDate || undefined}
                                pt={{
                                    input: { style: { fontSize: '0.7rem' } },
                                    dropdownButton: { style: { width: '2rem', padding: '0' } },
                                    icon: { style: { fontSize: '0.75rem' } }
                                }}
                            />
                        </div>

                        {/* End Date */}
                        <div className="flex-1" style={{ minWidth: '120px', maxWidth: '160px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>To Date</label>
                            <Calendar
                                value={endDate}
                                onChange={(e) => setEndDate(e.value)}
                                placeholder="To"
                                className="w-full p-inputtext-sm"
                                showIcon
                                inputStyle={{ fontSize: '0.7rem' }}
                                dateFormat="dd M yy"
                                showButtonBar
                                minDate={startDate || undefined}
                                pt={{
                                    input: { style: { fontSize: '0.7rem' } },
                                    dropdownButton: { style: { width: '2rem', padding: '0' } },
                                    icon: { style: { fontSize: '0.75rem' } }
                                }}
                            />
                        </div>

                        {/* View Toggle + Refresh - Right aligned, Desktop Only */}
                        <div className="hidden md:flex align-items-center gap-1 ml-auto flex-shrink-0">
                            <SelectButton
                                value={viewMode}
                                options={viewOptions}
                                onChange={(e) => e.value && setViewMode(e.value)}
                                optionLabel="icon"
                                itemTemplate={(option) => <i className={option.icon} style={{ fontSize: '0.7rem' }} />}
                                style={{ transform: 'scale(0.85)' }}
                            />
                            <Button
                                icon="pi pi-refresh"
                                className="p-button-text p-button-rounded"
                                onClick={handleRefresh}
                                loading={awaitingLoading || acceptedLoading}
                                tooltip="Refresh"
                                tooltipOptions={{ position: 'top' }}
                                style={{ width: '1.75rem', height: '1.75rem' }}
                            />
                        </div>
                    </div>

                    {/* Active filters & Results count */}
                    <div className="flex align-items-center justify-content-between mt-2 pt-2 border-top-1 surface-border">
                        <span className="text-500" style={{ fontSize: '0.7rem' }}>
                            {awaitingLoading ? 'Loading...' : `${activeTab === 0 ? awaitingPagination.total : acceptedPagination.total} result${(activeTab === 0 ? awaitingPagination.total : acceptedPagination.total) !== 1 ? 's' : ''}`}
                            {hasActiveFilters && ` \u2022 filters active`}
                        </span>
                        {hasActiveFilters && (
                            <Button
                                label="Clear"
                                icon="pi pi-filter-slash"
                                className="p-button-text p-button-sm"
                                onClick={clearFilters}
                                style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Summary Pills - Inline compact */}
            <div className="flex flex-wrap gap-2 mb-2">
                <div className="surface-card border-round shadow-1 px-3 py-2 flex align-items-center gap-2">
                    <span className="font-bold text-warning" style={{ fontSize: '0.9rem' }}>{summary.pending || 0}</span>
                    <span className="text-500" style={{ fontSize: '0.7rem' }}>Awaiting</span>
                </div>
                <div className="surface-card border-round shadow-1 px-3 py-2 flex align-items-center gap-2">
                    <span className="font-bold text-green-600" style={{ fontSize: '0.9rem' }}>{summary.interested || 0}</span>
                    <span className="text-500" style={{ fontSize: '0.7rem' }}>Accepted</span>
                </div>
                <div className="surface-card border-round shadow-1 px-3 py-2 flex align-items-center gap-2">
                    <span className="font-bold text-red-500" style={{ fontSize: '0.9rem' }}>{summary.declined || 0}</span>
                    <span className="text-500" style={{ fontSize: '0.7rem' }}>Declined</span>
                </div>
                <div className="surface-card border-round shadow-1 px-3 py-2 flex align-items-center gap-2">
                    <span className="font-bold text-primary" style={{ fontSize: '0.9rem' }}>{summary.total || 0}</span>
                    <span className="text-500" style={{ fontSize: '0.7rem' }}>Total</span>
                </div>
            </div>

            {/* Invites Content - TabView with Lazy Loading - Compact */}
            <TabView
                activeIndex={activeTab}
                onTabChange={handleTabChange}
                pt={{
                    navContainer: { style: { fontSize: '0.8rem' } },
                    inkbar: { style: { height: '2px' } }
                }}
            >
                {/* Awaiting Response Tab */}
                <TabPanel
                    header={
                        <span className="flex align-items-center gap-2">
                            Awaiting
                            {summary.pending > 0 && <Badge value={summary.pending} severity="warning" style={{ fontSize: '0.6rem', minWidth: '1.1rem', height: '1.1rem', lineHeight: '1.1rem' }} />}
                        </span>
                    }
                    pt={{ headerAction: { style: { padding: '0.5rem 0.85rem', fontSize: '0.8rem' } } }}
                >
                    {renderAwaitingInvites()}
                </TabPanel>

                {/* Accepted Tab */}
                <TabPanel
                    header={
                        <span className="flex align-items-center gap-2">
                            Accepted
                            {summary.interested > 0 && <Badge value={summary.interested} severity="success" style={{ fontSize: '0.6rem', minWidth: '1.1rem', height: '1.1rem', lineHeight: '1.1rem' }} />}
                        </span>
                    }
                    pt={{ headerAction: { style: { padding: '0.5rem 0.85rem', fontSize: '0.8rem' } } }}
                >
                    {renderAcceptedInvites()}
                </TabPanel>

                {/* Declined Tab */}
                <TabPanel
                    header={
                        <span className="flex align-items-center gap-2">
                            Declined
                            {declinedInvites.length > 0 && <Badge value={declinedInvites.length} severity="secondary" style={{ fontSize: '0.6rem', minWidth: '1.1rem', height: '1.1rem', lineHeight: '1.1rem' }} />}
                        </span>
                    }
                    pt={{ headerAction: { style: { padding: '0.5rem 0.85rem', fontSize: '0.8rem' } } }}
                >
                    {declinedLoading ? renderSkeleton() : (
                        declinedInvites.length > 0 ? (
                            viewMode === 'list' ? (
                                <div className="flex flex-column">
                                    {declinedInvites.map(invite => (
                                        <DeclinedInviteRow key={invite.id} invite={invite} />
                                    ))}
                                </div>
                            ) : (
                                <div className="grid">
                                    {declinedInvites.map(invite => (
                                        <div key={invite.id} className="col-12 md:col-6 lg:col-4 p-2">
                                            <DeclinedInviteCard invite={invite} />
                                        </div>
                                    ))}
                                </div>
                            )
                        ) : renderEmpty('declined')
                    )}
                </TabPanel>
            </TabView>

            {/* Review Interest Dialog */}
            <ReviewInterestDialog
                visible={reviewDialogVisible}
                onHide={() => {
                    setReviewDialogVisible(false);
                    setSelectedInterest(null);
                }}
                interest={selectedInterest}
                onSuccess={handleReviewSuccess}
            />

            {/* Compact Calendar overrides - font-size and icon sizing */}
            <style>{`
                .sent-invites .p-calendar.p-inputtext-sm .p-inputtext {
                    font-size: 0.7rem !important;
                }
                .sent-invites .p-calendar.p-inputtext-sm .p-datepicker-trigger {
                    width: 2rem !important;
                    padding: 0 !important;
                }
                .sent-invites .p-calendar.p-inputtext-sm .p-datepicker-trigger .p-button-icon,
                .sent-invites .p-calendar.p-inputtext-sm .p-datepicker-trigger .p-icon {
                    font-size: 0.75rem !important;
                    width: 0.75rem !important;
                    height: 0.75rem !important;
                }
            `}</style>
        </div>
    );
};

export default SentInvites;
